/**
 * Promo Codes (Marketing) Page
 * Lists promo codes, supports create/edit, and shows per-code redemption analytics.
 */

import { Fragment, useState } from 'react';
import { Ticket, Plus, BarChart3 } from 'lucide-react';
import { useI18n } from '@/i18n';
import { cn } from '@/lib/cn';
import { Button, Badge } from '@/components/ui';
import type { BadgeProps } from '@/components/ui';
import { parseBackendDateTime } from '@/lib/format/datetime';
import {
  usePromoCodesQuery,
  usePromoCodeAnalyticsQuery,
} from '@/lib/queries/usePromoCodes';
import type {
  PromoCodeResponse,
  PromoCodeAudience,
  PromoCodeStatus,
} from '@/lib/api/promoCodes';
import { PromoCodeFormDialog } from '@/features/promo-codes/PromoCodeFormDialog';
import type { PromoCodesMessages } from '@/i18n/messages';

function formatDate(iso: string): string {
  const d = parseBackendDateTime(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function audienceLabel(audience: PromoCodeAudience, t: PromoCodesMessages): string {
  switch (audience) {
    case 'all_users':
      return t.audienceAllUsers;
    case 'never_subscribed':
      return t.audienceNeverSubscribed;
    case 'new_users':
      return t.audienceNewUsers;
    default:
      return audience;
  }
}

function statusBadge(
  status: PromoCodeStatus,
  t: PromoCodesMessages
): { label: string; variant: BadgeProps['variant'] } {
  switch (status) {
    case 'active':
      return { label: t.statusActive, variant: 'success' };
    case 'paused':
      return { label: t.statusPaused, variant: 'warning' };
    case 'archived':
      return { label: t.statusArchived, variant: 'secondary' };
    default:
      return { label: status, variant: 'outline' };
  }
}

function AnalyticsRow({ id, colSpan }: { id: string; colSpan: number }) {
  const { messages } = useI18n();
  const t = messages.promoCodesPage;
  const { data, isLoading, isError } = usePromoCodeAnalyticsQuery(id);

  return (
    <tr className="bg-muted/40">
      <td colSpan={colSpan} className="px-4 py-3">
        {isLoading && <p className="text-sm text-muted-foreground">{t.analyticsLoading}</p>}
        {isError && <p className="text-sm text-destructive">{t.analyticsError}</p>}
        {data && (
          <div className="flex flex-wrap items-center gap-6">
            <span className="text-sm font-medium text-foreground">{t.analyticsTitle}</span>
            <div className="flex items-baseline gap-2">
              <span className="text-xs text-muted-foreground">{t.analyticsRedeemers}</span>
              <span className="text-base font-semibold text-foreground">{data.redeemers}</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-xs text-muted-foreground">{t.analyticsConversions}</span>
              <span className="text-base font-semibold text-foreground">{data.conversions}</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-xs text-muted-foreground">{t.analyticsConversionRate}</span>
              <span className="text-base font-semibold text-foreground">
                {(data.conversion_rate * 100).toFixed(1)}%
              </span>
            </div>
          </div>
        )}
      </td>
    </tr>
  );
}

export function PromoCodesPage() {
  const { messages } = useI18n();
  const t = messages.promoCodesPage;

  const { data, isLoading, isError } = usePromoCodesQuery({ limit: 100, offset: 0 });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<PromoCodeResponse | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (code: PromoCodeResponse) => {
    setEditing(code);
    setDialogOpen(true);
  };

  const toggleAnalytics = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const COLUMN_COUNT = 9;
  const codes = data?.codes ?? [];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Ticket className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-foreground">{t.pageTitle}</h1>
            <p className="text-sm text-muted-foreground mt-1">{t.pageDescription}</p>
          </div>
        </div>
        <Button onClick={openCreate}>
          <Plus className="w-4 h-4 mr-1" />
          {t.createButton}
        </Button>
      </div>

      {/* Table */}
      <div className="bg-card text-card-foreground rounded-lg border border-border shadow-elevation-1 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="px-4 py-3 font-medium">{t.colCode}</th>
                <th className="px-4 py-3 font-medium">{t.colFreeDays}</th>
                <th className="px-4 py-3 font-medium">{t.colAudience}</th>
                <th className="px-4 py-3 font-medium">{t.colWindow}</th>
                <th className="px-4 py-3 font-medium">{t.colMaxUses}</th>
                <th className="px-4 py-3 font-medium">{t.colUsedCount}</th>
                <th className="px-4 py-3 font-medium">{t.colStatus}</th>
                <th className="px-4 py-3 font-medium">{t.colDates}</th>
                <th className="px-4 py-3 font-medium text-right">{t.colActions}</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={COLUMN_COUNT} className="px-4 py-8 text-center text-muted-foreground">
                    …
                  </td>
                </tr>
              )}
              {isError && (
                <tr>
                  <td colSpan={COLUMN_COUNT} className="px-4 py-8 text-center text-destructive">
                    {t.loadError}
                  </td>
                </tr>
              )}
              {!isLoading && !isError && codes.length === 0 && (
                <tr>
                  <td colSpan={COLUMN_COUNT} className="px-4 py-8 text-center text-muted-foreground">
                    {t.emptyState}
                  </td>
                </tr>
              )}
              {codes.map((code) => {
                const badge = statusBadge(code.status, t);
                const isExpanded = expandedId === code.id;
                return (
                  <Fragment key={code.id}>
                    <tr className="border-b border-border hover:bg-muted/30">
                      <td className="px-4 py-3 font-mono font-medium text-foreground">{code.code}</td>
                      <td className="px-4 py-3 text-foreground">{code.free_days}</td>
                      <td className="px-4 py-3 text-foreground">{audienceLabel(code.audience, t)}</td>
                      <td className="px-4 py-3 text-foreground">
                        {code.new_user_window_days != null
                          ? code.new_user_window_days
                          : t.windowNotApplicable}
                      </td>
                      <td className="px-4 py-3 text-foreground">
                        {code.max_uses != null ? code.max_uses : t.maxUsesUnlimited}
                      </td>
                      <td className="px-4 py-3 text-foreground">{code.used_count}</td>
                      <td className="px-4 py-3">
                        <Badge variant={badge.variant}>{badge.label}</Badge>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                        <div>{formatDate(code.redeem_start_at)}</div>
                        <div>{formatDate(code.redeem_end_at)}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleAnalytics(code.id)}
                            className={cn(isExpanded && 'text-primary')}
                          >
                            <BarChart3 className="w-4 h-4 mr-1" />
                            {isExpanded ? t.hideAnalytics : t.viewAnalytics}
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => openEdit(code)}>
                            {t.edit}
                          </Button>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && <AnalyticsRow id={code.id} colSpan={COLUMN_COUNT} />}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <PromoCodeFormDialog
        open={dialogOpen}
        editing={editing}
        onClose={() => setDialogOpen(false)}
      />
    </div>
  );
}
